'use client';

/**
 * LensCard — a generic left-rail scoping lens (Browse↔Filter toggle).
 *
 * Mode-agnostic primitive: it reads its OWN content_key from the placement
 * provider and pulls that card's `default_props` (browse_cards /
 * filter_cards) via useCardProps. So any mode registers a lens content_key
 * + default_props and gets the toggle + card-list rendering — zero React
 * change. mode4 mounts it as `catalogue_lens` (vendor tree ↔ product
 * search); a Projects mode would mount it as e.g. `workspace_lens`
 * (project/RFQ tree ↔ scoped search).
 *
 * The frontend holds ONLY view-state (which lens is showing) and renders
 * the declared card-lists through EXPLORER_CARD_REGISTRY. Each mounted
 * child gets its own CardPlacementProvider slot_id (collection-card
 * pattern, collision-free capture).
 *
 * NOTE (interim): the auto-flip-to-Filter + facet-count badge read the
 * product-search store — the only search-state source today. When the
 * search generalizes to a type-scoped `search` (its own store keyed by
 * type), this reads that instead; the card-list composition above is
 * already fully generic.
 *
 * @see feedback_cards-surface-agnostic-relocatable
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain explorer
 */

import { useEffect, useMemo, useState } from 'react';
import { Store, SlidersHorizontal, Download } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useProductSearchStore } from '@/lib/stores/product-search-store';
import {
  useProductLabel,
  useCardProps,
  useAffordance,
  useTotalMatrixStore,
} from '@/lib/stores/total-matrix-store';
import { useLayoutStore } from '@/lib/stores/layout-store';
import { useLanguageStore } from '@/lib/stores/language-store';
import {
  CardPlacementProvider,
  useCardExport,
  useCardPlacement,
  downloadCapture,
} from '@/lib/design/card-export';
import { cn } from '@/lib/utils/cn';
import { EXPLORER_CARD_REGISTRY } from './card-registry';

type Lens = 'browse' | 'filter';

function LensButton({
  active,
  onClick,
  icon: Icon,
  label,
  badge,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Store;
  label: string;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex flex-1 items-center justify-center gap-1.5 rounded px-2 py-1 text-xs transition-colors',
        active
          ? 'bg-background text-foreground shadow-sm'
          : 'text-muted-foreground hover:text-foreground',
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
      {badge != null && badge > 0 && (
        <span className="rounded-full bg-primary px-1.5 text-[10px] text-primary-foreground">
          {badge}
        </span>
      )}
    </button>
  );
}

export function LensCard() {
  const query = useProductSearchStore((s) => s.query);
  const filters = useProductSearchStore((s) => s.filters);
  const [lens, setLens] = useState<Lens>('browse');

  // Read own content_key (== slot_id for a top-level explorer card) so this
  // primitive is mode-agnostic; the card-lists come from the matrix
  // (@card default_props), not a hardcoded React branch.
  const placement = useCardPlacement();
  const contentKey = placement?.slot_id ?? 'catalogue_lens';
  const surface = placement?.surface ?? 'explorer';
  const props = useCardProps(contentKey);
  const browseCards = (props.browse_cards as string[] | undefined) ?? [];
  const filterCards = (props.filter_cards as string[] | undefined) ?? [];

  // Per-lens label override — mode11's toggle now picks a TREE (Product
  // vs Vendor), not Browse vs Filter, since Filter no longer holds a
  // search/facet panel (product_search was hoisted out to its own
  // top-level explorer card, 2026-10-04). lens_browse/lens_filter stay
  // the default for every lens that doesn't override — mode4's
  // catalogue_lens sets neither prop, so it keeps "Browse"/"Filter"
  // unchanged. The hook is called unconditionally either way (Rules of
  // Hooks) — only the KEY it resolves varies per lens.
  const labelBrowse = useProductLabel((props.browse_label as string | undefined) ?? 'lens_browse');
  const labelFilter = useProductLabel((props.filter_label as string | undefined) ?? 'lens_filter');

  // The badge promises "see what's active in the Filter tab" — true only
  // when that tab actually displays facets (product_facets). mode11's
  // filter_cards is search-box-only (facets suppressed, shown on the
  // canvas instead per the Hybrid Filters experiment), so the badge would
  // just be a second, context-free "3" duplicating the canvas's own
  // Applied chips. Generic on filter_cards' own composition, not a
  // mode11 special-case — reappears automatically for any mode (mode4
  // included) whose filter_cards still include product_facets.
  const activeCount = filterCards.includes('product_facets') ? Object.keys(filters).length : 0;

  // Auto-flip to Filter when a search becomes active. Manual toggle still
  // lets the user return to Browse; it only re-flips on the next query.
  useEffect(() => {
    if (query.trim()) setLens('filter');
  }, [query]);

  // Scene-capture trigger, reachable from the explorer while an overlay
  // covers the canvas (the report-tile kebab lives on canvas cards the
  // overlay hides). Same `downloadCapture` path the report-tile uses,
  // but its own dedicated affordance (explorer.lens.download_capture,
  // ui_config/composition/affordances/lens.py) — this button used to
  // borrow explorer.report_tile.download_capture as a proxy, gated
  // correctly by coincidence (both admin-only) but a wrong address:
  // LensCard mounts as workspace_lens/catalogue_lens/etc., never as a
  // report_tile, so the derived test_id lied about the surface. The
  // lens is always "current state", so this mirrors the tile's
  // already-active branch: no navigation, resolved_selection falls
  // back to the matrix default. mode = the canonical key the
  // envelope's scope_summary wants.
  const queryClient = useQueryClient();
  const mode = useLayoutStore((s) => s.activeExplorerView);
  const currentSelection = useLayoutStore((s) => s.explorerSelection);
  const setExplorerSelection = useLayoutStore((s) => s.setExplorerSelection);
  const defaultSelection = useTotalMatrixStore(
    (s) => s.matrix?.[mode]?.default_selection ?? null,
  );
  const lang = useLanguageStore((s) => s.lang);
  const captureTestId = useAffordance('explorer.lens.download_capture');
  const captureLabel = useProductLabel('action_download_capture');

  const handleCapture = async () => {
    const resolvedSelection = currentSelection ?? defaultSelection;
    const source: 'explicit' | 'default' | 'unknown' =
      currentSelection !== null
        ? 'explicit'
        : resolvedSelection !== null
          ? 'default'
          : 'unknown';
    try {
      await downloadCapture(queryClient, {
        mode: mode ?? null,
        selection: currentSelection,
        resolved_selection: resolvedSelection,
        selection_source: source,
        viewer_lang: lang ?? 'en',
      });
    } catch (err) {
      // Strategy-only, no offline fallback — the error is the diagnostic.
      console.error('[LensCard] capture failed:', err);
    }
  };

  useCardExport(
    useMemo(
      () => ({
        label: 'Lens',
        content_key: contentKey,
        status: 'loaded' as const,
        query_key: null,
        text: `lens: ${lens}${activeCount ? ` · ${activeCount} filters` : ''}`,
        json: { lens, active_facets: Object.keys(filters) },
        state: { lens },
      }),
      [lens, activeCount, filters, contentKey],
    ),
  );

  const activeCards = lens === 'browse' ? browseCards : filterCards;

  // Per-lens canvas-drive override. Undeclared (mode4's catalogue_lens)
  // → the toggle stays pure rail view-state, as it always has — Browse/
  // Filter never touched the canvas because mode4 only ever had ONE
  // landing view (vendor_catalog), so there was nothing to switch
  // between. mode11's search_lens now has two equally-weighted trees
  // (category_catalog / vendor_catalog), so switching trees needs to
  // flip the canvas the same way clicking an actual row does —
  // undefined (not passed) means "leave the canvas alone", explicit
  // `null` means "clear back to the mode's no-selection default".
  const browseSelection = props.browse_selection as string | null | undefined;
  const filterSelection = props.filter_selection as string | null | undefined;
  const pickLens = (next: Lens) => {
    setLens(next);
    const sel = next === 'browse' ? browseSelection : filterSelection;
    if (sel !== undefined) setExplorerSelection(sel);
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-1.5">
        <div className="flex flex-1 items-center gap-1 rounded-lg bg-muted/40 p-1">
          <LensButton
            active={lens === 'browse'}
            onClick={() => pickLens('browse')}
            icon={Store}
            label={labelBrowse}
          />
          <LensButton
            active={lens === 'filter'}
            onClick={() => pickLens('filter')}
            icon={SlidersHorizontal}
            label={labelFilter}
            badge={activeCount}
          />
        </div>
        {captureTestId && (
          <button
            type="button"
            onClick={handleCapture}
            aria-label={captureLabel}
            title={captureLabel}
            data-testid={captureTestId}
            className="shrink-0 rounded p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
          >
            <Download className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      <div className="space-y-2">
        {activeCards.map((key) => {
          const Card = EXPLORER_CARD_REGISTRY[key];
          if (!Card) return null;
          return (
            <CardPlacementProvider key={key} value={{ surface, slot_id: key }}>
              <Card />
            </CardPlacementProvider>
          );
        })}
      </div>
    </div>
  );
}
